import { ICssProperty, CssProperties, ICssPropertiesObject } from "../models/css-property";
import { Color } from "../models/color";

export class CssUtil {

    public static replaceRgbToHex(css: string): string {
        if (!css) return css;
        let matches: RegExpExecArray | null;
        let result = css;
        const rgbaRegex = /rgba\(\d{1,3},\s*\d{1,3},\s*\d{1,3},\s*\d*.\d*?\)|rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)/gi;
        do {
            matches = rgbaRegex.exec(css);
            if (!matches) break;
            result = result.replace(matches[0], new Color(matches[0]).toHex());
        } while(matches);

        return result;
    }

    public static extractColors(css: string): string[] | null {
        if (!css) return null;
        const rgbaRegex = /rgba\(\d{1,3},\s*\d{1,3},\s*\d{1,3},\s*\d*.\d*?\)|rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)|#[0-9a-fA-F]{6}/gi;
        const res: string[] = [];
        let m: string[] | null;
        do {
            m = rgbaRegex.exec(css);
            if (m) res.push(m[0]);
        } while (m);
        return res;
    }

    public static parseFontFamily(val?: string): string {
        if (val) {
            let fFamily = val;
            if (fFamily.startsWith("\"") && fFamily.endsWith("\"")) {
                fFamily = fFamily.substring(1, fFamily.length - 1);
            }
            return fFamily;
        }
        return "";
    }

    public static getComputedStyle(element: HTMLElement | null): CSSStyleDeclaration | null {
        return element ? window.getComputedStyle(element) : null;
    }

    public static setStyleIn(selector: string, sheet?: CSSStyleSheet | null, properties?: ICssProperty[]): void {
        if (!sheet) {
            return;
        }

        this.removeRuleIn(selector, sheet);
        if (properties?.length) {
            const style = properties.filter(x => x.value).map(x => CssProperties.format(x)).join(";");
            if (style) {
                try {
                    sheet.insertRule(`${selector} {${style}}`);
                } catch (e) {
                    console.debug(e);
                }
            }
        }
    }

    public static setMediaStyleIn(condition: string, selector: string, sheet?: CSSStyleSheet | null, properties?: ICssProperty[]): void {
        if (!sheet) {
            return;
        }

        this.removeMediaRuleIn(condition, sheet);
        if (properties?.length) {
            const style = properties.filter(x => x.value).map(x => CssProperties.format(x)).join(";");
            if (style) {
                try {
                    sheet.insertRule(`@media ${condition} { ${selector} {${style}} }`);
                } catch (e) {
                    console.debug(e);
                }
            }
        }
    }

    public static setStyleInObject(selector: string, sheet?: CSSStyleSheet | null, properties?: ICssPropertiesObject): void {
        if (!sheet) {
            return;
        }

        this.removeRuleIn(selector, sheet);
        const styles: string[] = [];

        for (const propName in properties) {
            const value = properties[propName];
            if (propName !== "content") {
                if (value) styles.push(`${propName}:${value}`);
            } else {
                styles.push(`${propName}:"${value}"`);
            }
        }
        const style = styles.join(";");
        if (style) {
            try {
                sheet.insertRule(`${selector} {${style}}`);
            } catch (e) {
                console.debug(e);
            }
        }
    }

    public static removeRuleIn(selector: string, sheet?: CSSStyleSheet | null): void {
        if (!sheet) {
            return;
        }

        const index = this.findRuleIndex(selector, sheet);
        if (index > -1) {
            sheet.deleteRule(index);
        }
    }

    public static removeMediaRuleIn(condition: string, sheet?: CSSStyleSheet | null): void {
        if (!sheet) {
            return;
        }

        const index = this.findMediaRuleIndex(condition, sheet);
        if (index > -1) {
            sheet.deleteRule(index);
        }
    }

    public static getRuleIn(selector: string, sheet?: CSSStyleSheet | null): CSSStyleDeclaration | null {
        if (!sheet) {
            return null;
        }

        const index = this.findRuleIndex(selector, sheet);
        if (index < 0) return null;
        const rule = sheet.cssRules[index] as CSSStyleRule;
        return rule?.style;
    }

    public static flushSheet(styles: HTMLStyleElement): void {
        const sheet = styles.sheet;
        let content = "";

        if (sheet) {
            for (let i = 0; i < sheet.cssRules.length; i++) {
                const rule = sheet.cssRules.item(i);
                if (!rule) continue;
                content += rule.cssText;
                content += " ";
            }
        }

        styles.textContent = content;
    }

    public static findRuleIndex(selector: string, sheet?: CSSStyleSheet | null): number {
        if (!sheet || !sheet?.cssRules) {
            return -1;
        }

        for (let i = 0; i < sheet.cssRules.length; i++) {
            const rule = sheet.cssRules.item(i) as CSSStyleRule;
            if (rule && rule.selectorText === selector) return i;
        }
        return -1;
    }

    public static findMediaRuleIndex(condition: string, sheet?: CSSStyleSheet | null): number {
        if (!sheet) {
            return -1;
        }

        for (let i = 0; i < sheet.cssRules.length; i++) {
            const rule = sheet.cssRules.item(i) as CSSMediaRule;
            if (rule && rule.conditionText === condition) return i;
        }
        return -1;
    }

    public static setRootVariablesInSheet(sheet: CSSStyleSheet | null, properties?: ICssPropertiesObject): void {
        if (!sheet || !properties) {
            console.warn("Invalid stylesheet or properties provided.");
            return;
        }

        let rootRuleIndex = this.findRuleIndex(":root", sheet);
        let rootRule: CSSStyleRule | null = null;

        if (rootRuleIndex === -1) {
            try {
                sheet.insertRule(":root {}", sheet.cssRules.length);
                rootRuleIndex = sheet.cssRules.length - 1;
                rootRule = sheet.cssRules[rootRuleIndex] as CSSStyleRule;
            } catch (e) {
                console.error("Failed to create :root rule:", e);
                return;
            }
        } else {
            rootRule = sheet.cssRules[rootRuleIndex] as CSSStyleRule;
        }

        const style = rootRule.style;
        Object.entries(properties).forEach(([propName, value]) => {
            if (value !== undefined && value !== null) {
                style.setProperty(propName, value);
            }
        });
    }

}
